import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
    shelfApi,
    Shelf,
    ShelfRow,
    ShelfRowWhRow,
    ShelfRowBay,
    ShelfRowFloor,
    ShelfRowPallet,
    CreateShelfPayload,
    UpdateShelfPayload,
    CreateShelfRowPayload,
    UpdateShelfRowPayload,
    CreateShelfRowWhRowPayload,
    UpdateShelfRowWhRowPayload,
    CreateShelfRowBayBinPayload,
    UpdateShelfRowBayBinPayload,
    CreateShelfRowFloorPayload,
    UpdateShelfRowFloorPayload,
    CreateShelfRowPalletPayload,
    UpdateShelfRowPalletPayload,
    BulkOperationPayload,
    AddLevelInBayPayload,
    UpdateShelfRowBayPayload,
    UpdateShelfBayLevelNamePayload,
} from "../apis/shelveApis";

// Validation schemas for JSON payloads
export const CreateShelfPayloadSchema = z.object({
    warehouseId: z.string().min(1, "Warehouse ID is required"), 
    name: z.string().optional(),
})

const UpdateShelfPayloadSchema = z.object({
    location: z.object({ lat: z.number(), lng: z.number() }).optional(),
    name: z.string().optional(),
    isActive: z.boolean().optional(),
});

export const CreateShelfRowPayloadSchema = z.union([
    // Row creation schema
    z.object({
        whAreaId: z.string().min(1, "Warehouse ID is required"),
        rowName: z.string().min(1, "Row Name is required"),
        rowNumbers: z.string().min(1, "Row Numbers is required"),
        storageType: z.literal("Rows"),
    }),
    // Rack/Level creation schema
    z.object({
        whAreaId: z.string().min(1, "Warehouse ID is required"),
        whRowId: z.string().min(1, "Row ID is required"),
        rackName: z.string().min(1, "Rack name is required"),
        rackNumbers: z.string().min(1, "Rack numbers is required"),
        rackLevelName: z.string().min(1, "Rack level name is required"),
        rackLevelNumbers: z.string().min(1, "Rack level numbers is required"),
        storageType: z.enum(["Shelves", "Bins"]),
    }),
]);

const UpdateShelfRowPayloadSchema = z.object({
    whAreaId: z.string().optional(),
    whRackId: z.string().optional(),
    whRowId: z.string().min(1, "Row ID is required"),
    name: z.string().optional(),
    isActive: z.boolean().optional(),
    levels: z.any().optional(),
});

const UpdateShelfRowBayBinPayloadSchema = z.object({
    name: z.string().optional(),
    id: z.string().min(1, "Bin ID is required"),
});

export const UpdateShelfNamePayloadSchema = z.object({
    name: z.string().optional(),
});

export const UpdateShelfBayLevelNamePayloadSchema = z.object({
    id: z.string().min(1, "Level ID is required"),
    name: z.string().optional(),
});

const CreateShelfRowFloorPayloadSchema = z.object({
    whAreaId: z.string().min(1, "Area ID is required"),
    floorName: z.string().min(1, "Floor Slot Name is required"),
    floorNumbers: z.string().min(1, "Floor Slot Number is required"),
    whRowId: z.string().optional(),
    whRackId: z.string().optional(),
    position: z.string().optional(),
});

const UpdateShelfRowFloorPayloadSchema = z.object({
    name: z.string().optional(),
    id: z.string().min(1, "Floor ID is required"),
});

// export const CreateShelfRowPalletPayloadSchema = z.discriminatedUnion("palletType", [
//     // 👉 Shelves Pallet Payload
//     z.object({
//         // palletType: z.literal("SHELVES"),
//         rackId: z.string().min(1, "Rack ID is required"),
//         // bayId: z.string().min(1, "Bay ID is required"),
//         warehouseId: z.string().min(1, "Warehouse ID is required"),
//         shelveId: z.string().min(1, "Shelf ID is required"),
//         rackInstanceId: z.string().min(1, "Row Instance ID is required"),
//         clientId: z.string().min(1, "Client ID is required"),
//         // shelveLevels: z.array(
//         //     z.object({
//         //         numberOfPallets: z.string().optional(), // optional if coming from DB
//         //         level: z.string().optional(),
//         //         selected: z.boolean(),
//         //     })
//         // ).min(1, "At least one level must be selected"),
//         palletName: z.string().min(1, "Pallet Name is required"),
//         numberOfPallets: z.string().min(1, "Number of pallets must be at least 1"),
//     }),

//     // 👉 Floor Pallet Payload
//     z.object({
//         palletType: z.literal("FLOOR"),
//         rowId: z.string().min(1, "Row ID is required"),
//         // bayId: z.string().min(1, "Bay ID is required"),
//         warehouseId: z.string().min(1, "Warehouse ID is required"),
//         shelveId: z.string().min(1, "Shelf ID is required"),
//         rowInstanceId: z.string().min(1, "Row Instance ID is required"),
//         clientId: z.string().min(1, "Client ID is required"),
//         palletName: z.string().min(1, "Pallet Name is required"),
//         numberOfPallets: z.string().min(1, "Number of pallets must be at least 1"),
//         floorPalletPrefix: z.string().min(1, "Floor Pallet Prefix is required"),
//     }),
// ]);
export const CreateShelfRowPalletPayloadSchema = z.object({
    rackId: z.string().min(1, "Rack ID is required"),
    warehouseId: z.string().min(1, "Warehouse ID is required"),
    shelveId: z.string().min(1, "Shelf ID is required"),
    rackInstanceId: z.string().min(1, "Rack Instance ID is required"),
    clientId: z.string().min(1, "Client ID is required"),
    palletName: z.string().min(1, "Pallet Name is required"),
    numberOfPallets: z.string().min(1, "Number of pallets must be at least 1"),
});

const UpdateShelfRowPalletPayloadSchema = z.object({
    name: z.string().optional(),
    id: z.string().min(1, "Pallet ID is required"),
});

const BulkOperationPayloadSchema = z.object({
    ids: z.array(z.string().min(1)).min(1, "At least one ID is required"),
    isActive: z.boolean().optional(),
});

export const CreateShelfRowWhRowPayloadSchema = z.object({
    warehouseId: z.string().min(1, "Warehouse ID is required"),
    whAreaId: z.string().min(1, "Area ID is required"),
    name: z.string().min(1, "Row name is required").max(20, "Name must be less than 20 characters"),
    rowNumbers: z.string().min(1, "Row numbers are required").regex(/^\d+(,\d+)*$/, "Row numbers must be comma-separated numbers (e.g., 1,2,3)"),
});

const UpdateShelfRowWhRowPayloadSchema = z.object({
    name: z.string().min(1, "Row name is required").max(20, "Name must be less than 20 characters").optional(),
    status: z.enum(["ACTIVE", "INACTIVE"]).optional(),
});

// Corrected: Renamed `createMutation` to `useCreateMutation`
const useCreateMutation = <T, P>(
    mutationFn: (payload: P, isFormData?: boolean) => Promise<any>,
    successMessage: string,
    invalidateKeys: string[] = [],
    validator?: z.ZodSchema<P>,
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: P) => {
            if (validator) {
                validator.parse(payload);
            }
            const res = await mutationFn(payload);
            if (!res.success) {
                throw new Error(res.message || `${successMessage} failed`);
            }
            return res.data;
        },
        onSuccess: (data: T) => {
            console.log(`✅ ${successMessage}:`, data);
            invalidateKeys.forEach((key) =>
                queryClient.invalidateQueries({ queryKey: [key] }),
            );
        },
        onError: (error: Error) => {
            console.error(`❌ ${successMessage} failed:`, error.message);
        },
    });
};

// Corrected: The custom hook now takes the 'id' as a parameter directly.
const useCreateIdBasedMutation = <T, P>(
    mutationFn: (id: string, payload: P, isFormData?: boolean) => Promise<any>,
    successMessage: string,
    invalidateKeys: string[] = [],
    validator?: z.ZodSchema<P>,
    id?: string,
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: P) => {
            if (!id) {
                throw new Error("ID is required");
            }
            if (validator) {
                validator.parse(payload);
            }
            const res = await mutationFn(id, payload);
            if (!res.success) {
                throw new Error(res.message || `${successMessage} failed`);
            }
            return res.data;
        },
        onSuccess: (data: T) => {
            console.log(`✅ ${successMessage}:`, data);
            invalidateKeys.forEach((key) =>
                queryClient.invalidateQueries({ queryKey: [key] }),
            );
        },
        onError: (error: Error) => {
            console.error(`❌ ${successMessage} failed:`, error.message);
        },
    });
};

export const useShelfCreateMutation = () =>
    useCreateMutation<Shelf, CreateShelfPayload>(
        shelfApi.createShelf,
        "Shelf created successfully",
        ["shelfList", "shelfAllData"],
        CreateShelfPayloadSchema,
    );

export const useShelfCreateFormDataMutation = () =>
    useCreateMutation<Shelf, Record<string, any>>(
        (payload: any) => shelfApi.createShelf(payload, true), // Correctly pass the isFormData flag
        "Shelf created successfully (FormData)",
        ["shelfList", "shelfAllData"],
    );

export const useShelfListData = (page: number, searchFor: string) => {
    return useQuery({
        queryKey: ["shelfList", page, searchFor],
        queryFn: async () => {
            const res = await shelfApi.getShelves(page, searchFor);
            if (!res.success) {
                throw new Error(res.message || "Failed to fetch shelves");
            }
            return res.data;
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

export const useFetchShelfAllData = (warehouseId: string) => {
    return useQuery({
        queryKey: ["shelfAllData", warehouseId],
        queryFn: async () => {
            const res = await shelfApi.getAllShelves(warehouseId);
            if (!res.success) {
                throw new Error(res.message || "Failed to fetch shelves");
            }
            return res.data;
        },
        enabled: !!warehouseId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

export const useFetchShelfById = (id: string) => {
    return useQuery({
        queryKey: ["shelf", id],
        queryFn: async () => {
            const res = await shelfApi.getShelfById(id);
            if (!res.success) {
                throw new Error(res.message || "Failed to fetch shelf");
            }
            return res.data;
        },
        enabled: !!id,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

export const useDownloadShelveBarcode = () =>
  useMutation({
    mutationFn: async ({
      warehouseId,
      shelveId,
    }: {
      warehouseId: string;
      shelveId: string;
    }) => {
      const response = await shelfApi.downloadShelveBarcode(warehouseId, shelveId);
      return response.data; // ✅ return the actual data object
    },
  });

export const useShelfUpdateMutation = (id: string) =>
    useCreateIdBasedMutation<Shelf, UpdateShelfPayload>(
        shelfApi.updateShelf,
        "Shelf updated successfully",
        ["shelfList", "shelfAllData", "shelf"],
        UpdateShelfPayloadSchema,
        id
    );

export const useShelfUpdateFormDataMutation = (id: string) =>
    useCreateIdBasedMutation<Shelf, Record<string, any>>(
        (id, payload) => shelfApi.updateShelfFormData(id, payload), // Correctly pass the isFormData flag
        "Shelf updated successfully (FormData)",
        ["shelfList", "shelfAllData", "shelf"],
        undefined,
        id
    );

export const useShelfDeleteMutation = (id: string) =>
    useCreateIdBasedMutation<void, Record<string, any>>(
        shelfApi.deleteShelf,
        "Shelf deleted successfully",
        ["shelfList", "shelfAllData"],
        undefined,
        id
    );

export const useShelfBulkUpdateMutation = () =>
    useCreateMutation<void, BulkOperationPayload>(
        shelfApi.bulkUpdateShelves,
        "Shelves bulk updated successfully",
        ["shelfList", "shelfAllData"],
        BulkOperationPayloadSchema,
    );

export const useShelfRowCreateMutation = () =>
    useCreateMutation<ShelfRow, CreateShelfRowPayload>(
        shelfApi.createShelfRow,
        "Shelf row created successfully",
        ["shelfRowsAllData"],
        CreateShelfRowPayloadSchema,
    );

export const useShelfRowCreateFormDataMutation = () =>
    useCreateMutation<ShelfRow, Record<string, any>>(
        (payload: any) => shelfApi.createShelfRow(payload, true), // Correctly pass the isFormData flag
        "Shelf row created successfully (FormData)",
        ["shelfRowsAllData"],
    );

export const useFetchShelfRowsAllData = (shelfId: string, warehouseId: string) => {
    return useQuery({
        queryKey: ["shelfRowsAllData", shelfId, warehouseId],
        queryFn: async () => {
            const res = await shelfApi.getShelfRows(shelfId, warehouseId);
            if (!res.success) {
                return [];
            }
            return res.data;
        },
        enabled: !!shelfId && !!warehouseId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

export const useFetchShelfRowById = (id: string) => {
    return useQuery({
        queryKey: ["shelfRow", id],
        queryFn: async () => {
            const res = await shelfApi.getShelfRowById(id);
            if (!res.success) {
                throw new Error(res.message || "Failed to fetch shelf row");
            }
            return res.data;
        },
        enabled: !!id,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

export const useShelfRowUpdateMutation = (id: string) =>
    useCreateIdBasedMutation<ShelfRow, UpdateShelfRowPayload>(
        shelfApi.updateShelfRowWhRowName,
        "Row updated successfully",
        ["shelfRowsAllData", "shelfRow"],
        UpdateShelfRowPayloadSchema,
        id
    );

export const useShelfRowUpdateFormDataMutation = (id: string) =>
    useCreateIdBasedMutation<ShelfRow, Record<string, any>>(
        (id, payload) => shelfApi.updateShelfRowFormData(id, payload), // Correctly pass the isFormData flag
        "Shelf row updated successfully (FormData)",
        ["shelfRowsAllData", "shelfRow"],
        undefined,
        id
    );

export const useShelfRowDeleteMutation = (id: string) =>
    useCreateIdBasedMutation<void, Record<string, any>>(
        shelfApi.deleteShelfRow,
        "Shelf row deleted successfully",
        ["shelfRowsAllData"],
        undefined,
        id
    );

export const useShelfRowBulkUpdateMutation = () =>
    useCreateMutation<void, BulkOperationPayload>(
        shelfApi.bulkUpdateShelfRows,
        "Shelf rows bulk updated successfully",
        ["shelfRowsAllData"],
        BulkOperationPayloadSchema,
    );

export const useShelfRowBayBinCreateMutation = () =>
    useCreateMutation<ShelfRowBay, CreateShelfRowBayBinPayload>(
        shelfApi.createShelfRowBayBin,
        "Shelf row bay bin created successfully",
        ["shelfRowBayBins"],
        undefined,
    );

export const useShelfRowBayBinCreateFormDataMutation = () =>
    useCreateMutation<ShelfRowBay, Record<string, any>>(
        (payload: any) => shelfApi.createShelfRowBayBin(payload, true), // Correctly pass the isFormData flag
        "Shelf row bay bin created successfully (FormData)",
        ["shelfRowBayBins"],
    );

export const useRackLevelBinsCreateMutation = () =>
    useCreateMutation<ShelfRowBay, CreateShelfRowBayBinPayload>(
        shelfApi.createRackLevelBins,
        "Rack level bins created successfully",
        ["shelfRowBayBins"],
        undefined,
    );

export const useRackLevelPalletsCreateMutation = () =>
    useCreateMutation<ShelfRowPallet, CreateShelfRowPalletPayload>(
        shelfApi.createRackLevelPallets,
        "Rack level pallets created successfully",
        ["shelfRowPallets"],
        undefined,
    );

export const useRackLevelBinsUpdateMutation = () =>
    useCreateMutation<ShelfRowBay, CreateShelfRowBayBinPayload>(
        shelfApi.updateRackLevelBins,
        "Rack level bins updated successfully",
        ["shelfRowBayBins"],
        undefined,
    );

export const useRackLevelPalletsUpdateMutation = () =>
    useCreateMutation<ShelfRowPallet, CreateShelfRowPalletPayload>(
        shelfApi.updateRackLevelPallets,
        "Rack level pallets updated successfully",
        ["shelfRowPallets"],
        undefined,
    );

export const useFloorPalletsCreateMutation = () =>
    useCreateMutation<any, any>(
        shelfApi.createFloorPallets,
        "Floor pallets created successfully",
        ["floorPallets"],
        undefined,
    );

export const useFloorPalletsUpdateMutation = () =>
    useCreateMutation<any, any>(
        shelfApi.updateFloorPallets,
        "Floor pallets updated successfully",
        ["floorPallets"],
        undefined,
    );

export const useFetchShelfRowBayBins = (rowId: string) => {
    return useQuery({
        queryKey: ["shelfRowBayBins", rowId],
        queryFn: async () => {
            const res = await shelfApi.getShelfRowBayBins(rowId);
            if (!res.success) {
                return [];
            }
            return res.data;
        },
        enabled: !!rowId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

export const useFetchShelfRowBayBinById = (id: string) => {
    return useQuery({
        queryKey: ["shelfRowBayBin", id],
        queryFn: async () => {
            const res = await shelfApi.getShelfRowBayBinById(id);
            if (!res.success) {
                throw new Error(res.message || "Failed to fetch shelf row bay bin");
            }
            return res.data;
        },
        enabled: !!id,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

export const useShelfRowBayBinUpdateMutation = (id: string) =>
    useCreateIdBasedMutation<ShelfRowBay, UpdateShelfRowBayBinPayload>(
        shelfApi.updateShelfRowBayBin,
        "Shelf row bay bin updated successfully",
        ["shelfRowBayBins", "shelfRowBayBin"],
        UpdateShelfRowBayBinPayloadSchema,
        id
    );

export const useShelfRowBayBinUpdateFormDataMutation = (id: string) =>
    useCreateIdBasedMutation<ShelfRowBay, Record<string, any>>(
        (id, payload) => shelfApi.updateShelfRowBayBinFormData(id, payload),
        "Shelf row bay bin updated successfully (FormData)",
        ["shelfRowBayBins", "shelfRowBayBin"],
        undefined,
        id
    );

export const useShelfRowBayUpdateMutation = (id: string) =>
    useCreateIdBasedMutation<ShelfRowBay, UpdateShelfRowBayPayload>(
        shelfApi.updateShelfRowBay,
        "Shelf row bay updated successfully",
        ["shelfRowBayUpdate", id],
        UpdateShelfNamePayloadSchema,
        id
    );

export const useShelfRowBayDeleteMutation = (id: string) =>
    useCreateIdBasedMutation<void, Record<string, any>>(
        shelfApi.deleteShelfRowBay,
        "Shelf row bay deleted successfully",
        ["shelfRowBay"],
        undefined,
        id
    );

export const useShelfRowBayAddLevelMutation = (id: string) =>
    useCreateIdBasedMutation<ShelfRowBay, AddLevelInBayPayload>(
        shelfApi.addShelfRowBayLevel,
        "Shelf row bay level added successfully",
        ["addShelfRowBayLevel", id],
        undefined,
        id
    );

export const useShelfRowBayUpdateLevelMutation = (bayId: string) =>
    useCreateIdBasedMutation<ShelfRowBay, UpdateShelfBayLevelNamePayload>(
        shelfApi.updateShelfRowBayLevel,
        "Shelf row bay level updated successfully",
        ["updateShelfRowBayLevel"],
        UpdateShelfBayLevelNamePayloadSchema,
        bayId
    );

export const useShelfRowBayLevelDeleteMutation = (id: string) =>
    useCreateIdBasedMutation<void, Record<string, any>>(
        shelfApi.deleteShelfRowBayLevel,
        "Shelf row bay level deleted successfully",
        ["shelfRowBayLevel"],
        undefined,
        id
    );

export const useShelfRowBayBinDeleteMutation = (id: string) =>
    useCreateIdBasedMutation<void, Record<string, any>>(
        shelfApi.deleteShelfRowBayBin,
        "Shelf row bay bin deleted successfully",
        ["shelfRowBayBins"],
        undefined,
        id
    );

export const useShelfRowBayBinBulkUpdateMutation = () =>
    useCreateMutation<void, BulkOperationPayload>(
        shelfApi.bulkUpdateShelfRowBayBins,
        "Shelf row bay bins bulk updated successfully",
        ["shelfRowBayBins"],
        BulkOperationPayloadSchema,
    );

export const useShelfRowFloorCreateMutation = () =>
    useCreateMutation<ShelfRowFloor, CreateShelfRowFloorPayload>(
        shelfApi.createShelfRowFloor,
        "Shelf row floor created successfully",
        ["shelfAreaFloors"],
        CreateShelfRowFloorPayloadSchema,
    );

export const useShelfRowFloorCreateFormDataMutation = () =>
    useCreateMutation<ShelfRowFloor, Record<string, any>>(
        (payload: any) => shelfApi.createShelfRowFloor(payload, true), // Correctly pass the isFormData flag
        "Shelf row floor created successfully (FormData)",
        ["shelfAreaFloors"],
    );

export const useFetchShelfAreaFloors = (areaId: string) => {
    return useQuery({
        queryKey: ["shelfAreaFloors", areaId],
        queryFn: async () => {
            const res = await shelfApi.getShelfAreaFloors(areaId);
            if (!res.success) {
                return [];
            }
            return res.data;
        },
        enabled: !!areaId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

export const useFetchShelfRowFloorById = (id: string) => {
    return useQuery({
        queryKey: ["shelfRowFloor", id],
        queryFn: async () => {
            const res = await shelfApi.getShelfRowFloorById(id);
            if (!res.success) {
                throw new Error(res.message || "Failed to fetch shelf row floor");
            }
            return res.data;
        },
        enabled: !!id,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

export const useShelfRowFloorUpdateMutation = (id: string) =>
    useCreateIdBasedMutation<ShelfRowFloor, UpdateShelfRowFloorPayload>(
        shelfApi.updateShelfRowFloor,
        "Shelf row floor updated successfully",
        ["shelfRowFloors", "shelfRowFloor"],
        UpdateShelfRowFloorPayloadSchema,
        id
    );

export const useShelfRowFloorSlotUpdateMutation = (id: string) =>
    useCreateIdBasedMutation<ShelfRowFloor, UpdateShelfRowFloorPayload>(
        shelfApi.updateShelfRowFloorSlot,
        "Shelf row floor slot updated successfully",
        ["shelfRowFloors", "shelfRowFloor"],
        UpdateShelfRowFloorPayloadSchema,
        id
    );

export const useShelfRowFloorUpdateFormDataMutation = (id: string) =>
    useCreateIdBasedMutation<ShelfRowFloor, Record<string, any>>(
        (id, payload) => shelfApi.updateShelfRowFloorFormData(id, payload),
        "Shelf row floor updated successfully (FormData)",
        ["shelfRowFloors", "shelfRowFloor"],
        undefined,
        id
    );

export const useShelfRowFloorDeleteMutation = (id: string) =>
    useCreateIdBasedMutation<void, Record<string, any>>(
        shelfApi.deleteShelfRowFloor,
        "Shelf row floor deleted successfully",
        ["shelfRowFloors"],
        undefined,
        id
    );

export const useShelfRowFloorBulkUpdateMutation = () =>
    useCreateMutation<void, BulkOperationPayload>(
        shelfApi.bulkUpdateShelfRowFloors,
        "Shelf row floors bulk updated successfully",
        ["shelfRowFloors"],
        BulkOperationPayloadSchema,
    );

export const useShelfRowPalletCreateMutation = () =>
    useCreateMutation<ShelfRowPallet, CreateShelfRowPalletPayload>(
        shelfApi.createShelfRowPallet,
        "Shelf row pallet created successfully",
        ["shelfRowPallets"],
        CreateShelfRowPalletPayloadSchema,
    );

export const useShelfRowPalletCreateFormDataMutation = () =>
    useCreateMutation<ShelfRowPallet, Record<string, any>>(
        (payload: any) => shelfApi.createShelfRowPallet(payload, true), // Correctly pass the isFormData flag
        "Shelf row pallet created successfully (FormData)",
        ["shelfRowPallets"],
    );

export const useFetchShelfRowPallets = (rowId: string) => {
    return useQuery({
        queryKey: ["shelfRowPallets", rowId],
        queryFn: async () => {
            const res = await shelfApi.getShelfRowPallets(rowId);
            if (!res.success) {
                return [];
            }
            return res.data;
        },
        enabled: !!rowId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

export const useFetchShelfRowPalletById = (id: string) => {
    return useQuery({
        queryKey: ["shelfRowPallet", id],
        queryFn: async () => {
            const res = await shelfApi.getShelfRowPalletById(id);
            if (!res.success) {
                throw new Error(res.message || "Failed to fetch shelf row pallet");
            }
            return res.data;
        },
        enabled: !!id,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

export const useShelfRowPalletSlotUpdateMutation = (id: string) =>
    useCreateIdBasedMutation<ShelfRowPallet, UpdateShelfRowPalletPayload>(
        shelfApi.updateShelfRowPalletSlot,
        "Shelf row pallet updated successfully",
        ["shelfRowPallets", "shelfRowPallet"],
        UpdateShelfRowPalletPayloadSchema,
        id
    );

export const useShelfRowPalletUpdateMutation = (id: string) =>
    useCreateIdBasedMutation<ShelfRowPallet, UpdateShelfRowPalletPayload>(
        shelfApi.updateShelfRowPallet,
        "Shelf row pallet updated successfully",
        ["shelfRowPallets", "shelfRowPallet"],
        UpdateShelfRowPalletPayloadSchema,
        id
    );

export const useShelfRowPalletUpdateFormDataMutation = (id: string) =>
    useCreateIdBasedMutation<ShelfRowPallet, Record<string, any>>(
        (id, payload) => shelfApi.updateShelfRowPalletFormData(id, payload),
        "Shelf row pallet updated successfully (FormData)",
        ["shelfRowPallets", "shelfRowPallet"],
        undefined,
        id
    );

export const useShelfRowPalletDeleteMutation = (id: string) =>
    useCreateIdBasedMutation<void, Record<string, any>>(
        shelfApi.deleteShelfRowPallet,
        "Shelf row pallet deleted successfully",
        ["shelfRowPallets"],
        undefined,
        id
    );

export const useShelfRowPalletBulkUpdateMutation = () =>
    useCreateMutation<void, BulkOperationPayload>(
        shelfApi.bulkUpdateShelfRowPallets,
        "Shelf row pallets bulk updated successfully",
        ["shelfRowPallets"],
        BulkOperationPayloadSchema,
    );

// WhRows Hooks
export const useShelfRowWhRowCreateMutation = () =>
    useCreateMutation<ShelfRowWhRow, CreateShelfRowWhRowPayload>(
        shelfApi.createShelfRowWhRow,
        "Warehouse row created successfully",
        ["shelfRowWhRows"],
        CreateShelfRowWhRowPayloadSchema,
    );

export const useFetchShelfRowWhRows = (whAreaId: string) => {
    return useQuery({
        queryKey: ["shelfRowWhRows", whAreaId],
        queryFn: async () => {
            const res = await shelfApi.getShelfRowWhRows(whAreaId);
            if (!res.success) {
                return [];
            }
            return res.data;
        },
        enabled: !!whAreaId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

export const useShelfRowWhRowUpdateMutation = (id: string) =>
    useCreateIdBasedMutation<ShelfRowWhRow, UpdateShelfRowWhRowPayload>(
        shelfApi.updateShelfRowWhRowName,
        "Warehouse row updated successfully",
        ["shelfRowWhRows", "shelfRowWhRow"],
        UpdateShelfRowWhRowPayloadSchema,
        id
    );

export const useShelfRowWhRowUpdateFormDataMutation = (id: string) =>
    useCreateIdBasedMutation<ShelfRowWhRow, Record<string, any>>(
        (id, payload) => shelfApi.updateShelfRowWhRowName(id, payload),
        "Warehouse row updated successfully (FormData)",
        ["shelfRowWhRows", "shelfRowWhRow"],
        undefined,
        id
    );

export const useShelfRowWhRowDeleteMutation = (id: string) =>
    useCreateIdBasedMutation<void, Record<string, any>>(
        shelfApi.deleteShelfRowWhRow,
        "Warehouse row deleted successfully",
        ["shelfRowWhRows"],
        undefined,
        id
    );

export const useShelfRowWhRowBulkUpdateMutation = () =>
    useCreateMutation<void, BulkOperationPayload>(
        shelfApi.bulkUpdateShelfRowWhRows,
        "Warehouse rows bulk updated successfully",
        ["shelfRowWhRows"],
        BulkOperationPayloadSchema,
    );

export const useAdminRacksUpdateMutation = (areaId: string) =>
    useCreateIdBasedMutation<any, UpdateShelfRowPayload>(
        (areaId, payload) => shelfApi.updateAdminRacksWithLevels(areaId, payload),
        "Rack updated successfully",
        ["shelfRowsAllData"],
        UpdateShelfRowPayloadSchema,
        areaId
    );

export const useFetchLocationInventory = (locationId: string) => {
    return useQuery({
        queryKey: ["locationInventory", locationId],
        queryFn: async () => {
            const res = await shelfApi.getLocationInventory(locationId);
            if (!res.success) {
                return [];
            }
            return res.data;
        },
        enabled: !!locationId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

export const useFetchShelvesTemplate = () => {
    return useQuery({
        queryKey: ["shelvesTemplate"],
        queryFn: async () => {
            const res = await shelfApi.fetchShelvesTemplate();
            const isSuccess = res.success || (res as any).status === "success";
            if (!isSuccess) {
                throw new Error(res.message || "Failed to fetch shelves template");
            }
            return res.data;
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

export const useShelveBulkDocMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: FormData) => {
            const res = await shelfApi.bulkUploadShelves(payload);
            if (!res.success) {
                throw new Error(res.message || "Failed to bulk upload shelves");
            }
            return res.data;
        },
        onSuccess: (data: any) => {
            console.log("✅ Shelves bulk uploaded successfully:", data);
            queryClient.invalidateQueries({ queryKey: ["shelfList"] });
            queryClient.invalidateQueries({ queryKey: ["shelfAllData"] });
            queryClient.invalidateQueries({ queryKey: ["shelfRowsAllData"] });
            queryClient.invalidateQueries({ queryKey: ["shelfAreaFloors"] });
            queryClient.invalidateQueries({ queryKey: ["shelfRowBayBins"] });
            queryClient.invalidateQueries({ queryKey: ["shelfRowPallets"] });
            queryClient.invalidateQueries({ queryKey: ["shelfRowWhRows"] });
        },
        onError: (error: Error) => {
            console.error("❌ Shelves bulk upload failed:", error.message);
        },
    });
};